import * as React from 'react';

import cx from 'classnames';

import { Heading } from 'components/Heading';

import { FormStatus, Question, Waiver } from 'types/forms-and-waivers';

import Button from '../Button';
import BellIcon from '../Icons/Bell.svg';

import s from './FormsWaiversCta.module.css';

type AppointmentInfo = {
  appointmentGuid: string;
  formStatus?: FormStatus;
  questions?: Question[];
  thirdPartyFormUrl?: string | null;
  waivers?: Waiver[];
};

type TFormsWaiversCtaProps = {
  appointmentInfo: AppointmentInfo | AppointmentInfo[];
  subtitle: string;
  newTab?: boolean;
};

const FormsWaiversCta = ({
  appointmentInfo,
  newTab,
  subtitle,
}: TFormsWaiversCtaProps) => {
  const appointmentList = Array.isArray(appointmentInfo)
    ? appointmentInfo
    : [appointmentInfo];

  const appointmentsLinks = appointmentList.map((appt, index) => {
    const {
      appointmentGuid,
      formStatus,
      questions,
      waivers,
      thirdPartyFormUrl,
    } = appt;

    const links: {
      external?: boolean; // should contain external link icon
      newTab?: boolean;
      text: string;
      url: string;
    }[] = [];

    if (
      appointmentGuid &&
      formStatus &&
      formStatus !== FormStatus.not_required &&
      (questions?.length || waivers?.length)
    ) {
      links.push({
        url: `/a/${appt.appointmentGuid}/form`,
        text: `Form ${appointmentList.length > 1 ? index + 1 : ''}`,
        newTab,
      });
    }

    if (thirdPartyFormUrl) {
      links.push({
        url: thirdPartyFormUrl,
        text: `External Form ${appointmentList.length > 1 ? index + 1 : ''}`,
        newTab: true,
        external: true,
      });
    }

    return links;
  });

  const areLinksAvailable = appointmentsLinks.some((appt) => appt.length > 0);

  if (!areLinksAvailable) {
    return null;
  }

  return (
    <div className={s.root}>
      <BellIcon className={s.bellIcon} />
      <Heading variant="small">To do before your appointment</Heading>
      <p>{subtitle}</p>

      {appointmentsLinks.map(
        (links, index) =>
          links.length > 0 && (
            <div key={index} className={s.buttonSection}>
              {links[0] ? (
                <Button
                  to={links[0].url}
                  className={cx(s.primaryButton, {
                    [s.external]: links[0].external,
                  })}
                  newTab={links[0].newTab}
                >
                  {links[0].text}
                </Button>
              ) : null}

              {links[1] ? (
                <a
                  className={cx(s.secondaryButton, {
                    [s.external]: links[1].external,
                  })}
                  rel="noreferrer"
                  target="_blank"
                  href={links[1].url}
                >
                  {links[1].text}
                </a>
              ) : null}
            </div>
          ),
      )}
    </div>
  );
};

export { FormsWaiversCta };
